import type {
  Choice,
  Novel,
  NumericRequirement,
  Scene,
  StateRequirement,
  StoryState
} from "./types.js";

export interface RuntimeState {
  novel: Novel;
  currentSceneId: string;
  visitedSceneIds: string[];
  worldState: StoryState;
}

export function createRuntime(novel: Novel, startSceneId = novel.start_scene_id): RuntimeState {
  const startScene = assertSceneExists(novel, startSceneId);

  return {
    novel,
    currentSceneId: startSceneId,
    visitedSceneIds: [startSceneId],
    worldState: applyState({}, startScene.state_changes)
  };
}

/**
 * Applies a state patch onto a base state.
 *
 * Numeric values ACCUMULATE: a patch of `{ trust: 1 }` applied to `{ trust: 1 }`
 * yields `{ trust: 2 }`. This is what makes accumulating axes (trust/doubt/…)
 * and their `required_state` gates work — earlier choices that each add `+1`
 * build toward a threshold. A numeric key that is not yet present is simply set.
 *
 * Non-numeric values (string, boolean, array) OVERWRITE, so `player_location`
 * and status flags behave as before (target-scene changes win conflicts).
 */
export function applyState(base: StoryState, patch: StoryState | undefined): StoryState {
  if (!patch) {
    return base;
  }
  const result: StoryState = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const current = result[key];
    if (typeof value === "number" && typeof current === "number") {
      result[key] = current + value;
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function meetsRequirement(
  worldState: StoryState,
  required: StateRequirement | undefined
): boolean {
  if (!required) {
    return true;
  }

  return Object.entries(required).every(([key, value]) => {
    const current = worldState[key];

    if (isNumericRequirement(value)) {
      if (typeof current !== "number") {
        return false;
      }

      switch (value.op ?? "gte") {
        case "gte":
          return current >= value.value;
        case "gt":
          return current > value.value;
        case "lte":
          return current <= value.value;
        case "lt":
          return current < value.value;
        case "eq":
          return current === value.value;
      }
    }

    if (typeof value === "number") {
      return typeof current === "number" && current >= value;
    }

    if (Array.isArray(value)) {
      return Array.isArray(current) && value.every((item) => current.includes(item));
    }

    return current === value;
  });
}

export function getAvailableChoices(state: RuntimeState): Choice[] {
  const scene = getCurrentScene(state);
  return scene.choices.filter((choice) =>
    meetsRequirement(state.worldState, choice.required_state)
  );
}

export function getCurrentScene(state: RuntimeState): Scene {
  return assertSceneExists(state.novel, state.currentSceneId);
}

export function chooseNextScene(state: RuntimeState, choiceId: string): RuntimeState {
  const scene = getCurrentScene(state);
  const choice = scene.choices.find((candidate) => candidate.id === choiceId);

  if (!choice) {
    throw new Error(`Choice "${choiceId}" does not exist on scene "${scene.id}".`);
  }

  if (!meetsRequirement(state.worldState, choice.required_state)) {
    throw new Error(`Choice "${choiceId}" is not available in the current world state.`);
  }

  return goToScene(state, choice);
}

export function restartNovel(state: RuntimeState): RuntimeState {
  return createRuntime(state.novel);
}

export function isCurrentSceneEnding(state: RuntimeState): boolean {
  const scene = getCurrentScene(state);
  return (
    scene.is_ending ||
    state.novel.endings.some((ending) => ending.scene_id === state.currentSceneId)
  );
}

function goToScene(state: RuntimeState, choice: Choice): RuntimeState {
  const nextScene = assertSceneExists(state.novel, choice.next_scene_id);
  const afterChoice = applyState(state.worldState, choice.effects);

  return {
    ...state,
    currentSceneId: choice.next_scene_id,
    visitedSceneIds: [...state.visitedSceneIds, choice.next_scene_id],
    worldState: applyState(afterChoice, nextScene.state_changes)
  };
}

function isNumericRequirement(value: unknown): value is NumericRequirement {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "value" in value &&
    typeof (value as { value: unknown }).value === "number"
  );
}

function assertSceneExists(novel: Novel, sceneId: string): Scene {
  const scene = novel.scenes.find((candidate) => candidate.id === sceneId);

  if (!scene) {
    throw new Error(`Scene "${sceneId}" does not exist.`);
  }

  return scene;
}
